<template>
    <!--
    Оверлей для гостей: перекрывает ChatPage полностью и не даёт пользоваться
    интерфейсом до входа/регистрации. Идея — оставить «превью» продукта под
    оверлеем (пользователь видит, куда попал), но полностью заблокировать любые
    действия и запросы. См. также route guard в chatApi (жестко режем запросы
    без токена) и src/router/index.js (публичные / и /chat остаются
    отрисованными, но overlay поверх). Не имеет закрывающих контролов
    сознательно: только «Войти» / «Регистрация».
    -->
    <div
        class="guest-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-overlay-title"
    >
        <div class="guest-overlay__backdrop" aria-hidden="true"></div>
        <div class="guest-overlay__card">
            <div class="guest-overlay__brand">
                <img
                    :src="brandLogo"
                    alt="Автомаркетолог"
                    class="guest-overlay__logo"
                />
                <span class="guest-overlay__tag">AI-АНАЛИТИК</span>
            </div>
            <h2 id="guest-overlay-title" class="guest-overlay__title">
                Протестируй платформу в расширенном тарифе<br>
                <span class="guest-overlay__title-accent">совершенно бесплатно на 1 день</span>
                после регистрации
            </h2>
            <div class="guest-overlay__actions">
                <router-link
                    to="/register-first-step"
                    class="guest-overlay__btn guest-overlay__btn--primary"
                    @click="onRegisterClick"
                >
                    Зарегистрироваться
                </router-link>
                <router-link
                    :to="loginTo"
                    class="guest-overlay__btn guest-overlay__btn--ghost"
                    @click="onLoginClick"
                >
                    Уже есть аккаунт — войти
                </router-link>
            </div>
            <p class="guest-overlay__note">
                Регистрируясь, вы соглашаетесь с
                <router-link to="/privacy-policy" class="guest-overlay__link">
                    политикой конфиденциальности
                </router-link>.
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { reachGoal } from '@/utils/analytics'
import brandLogo from '@/assets/images/am_logo 1.svg'

const route = useRoute()

// Возвращаем пользователя туда, где он был (чат/лендинг), чтобы после логина
// не терять контекст (в частности query-параметры handoff).
const loginTo = computed(() => ({
    path: '/login',
    query: { redirect: route.fullPath || '/chat' },
}))

function onRegisterClick() {
    reachGoal('guest_click_register')
}

function onLoginClick() {
    reachGoal('guest_click_login')
}

onMounted(() => {
    reachGoal('guest_overlay_shown')
})
</script>

<style scoped>
.guest-overlay {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    /* pointer-events на самом контейнере — оверлей ловит всё, но клики по
       кнопкам внутри карточки работают штатно (кнопки — потомки). */
}

.guest-overlay__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(21, 26, 34, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
}

.guest-overlay__card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 460px;
    background: #ffffff;
    border-radius: 16px;
    padding: 32px 28px 24px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
    text-align: center;
}

.guest-overlay__brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
}

.guest-overlay__logo {
    height: 26px;
    width: auto;
    display: block;
}

.guest-overlay__tag {
    font-size: 10px;
    font-weight: 700;
    color: #888888;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    padding: 3px 8px;
    border: 1px solid #e0e0e0;
    border-radius: 999px;
    line-height: 1;
}

.guest-overlay__title {
    margin: 0 0 12px;
    font-size: 20px;
    line-height: 1.35;
    font-weight: 700;
    color: #23282C;
}

.guest-overlay__title-accent {
    color: #C23132;
}

.guest-overlay__actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 24px;
    margin-bottom: 16px;
}

.guest-overlay__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 18px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    border: 1px solid transparent;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease;
    cursor: pointer;
}

.guest-overlay__btn--primary {
    background: #E30613;
    color: #ffffff;
    box-shadow: 0 6px 16px rgba(227, 6, 19, 0.28);
}

.guest-overlay__btn--primary:hover {
    background: #c30410;
    box-shadow: 0 8px 20px rgba(227, 6, 19, 0.32);
}

.guest-overlay__btn--ghost {
    background: #ffffff;
    color: #23282C;
    border-color: #d0d3d8;
}

.guest-overlay__btn--ghost:hover {
    border-color: #23282C;
    background: #f7f7f7;
}

.guest-overlay__note {
    margin: 0;
    font-size: 11px;
    color: #888888;
    line-height: 1.5;
}

.guest-overlay__link {
    color: #23282C;
    text-decoration: underline;
}

.guest-overlay__link:hover {
    color: #000;
}

@media (max-width: 480px) {
    .guest-overlay__card {
        padding: 24px 20px 18px;
        border-radius: 14px;
    }

    .guest-overlay__title {
        font-size: 18px;
    }
}
</style>
